/**
 * [TAXONOMY: age_groups]
 *
 * Fetches age bucket structure from /api/v1/age-buckets/
 * and combines with localized labels from /api/v1/i18n/ (age_groups section).
 *
 * Architecture:
 * - Structure API: /api/v1/age-buckets/ returns { buckets: [{ id, is_child_meal }] }
 * - Labels API: /api/v1/i18n/ returns { ..., age_groups: { baby: { label }, ... } }
 *
 * NO FALLBACK: If either API fails, returns null. Component must show error.
 * See ADR 0014 — Alignement Backend/Frontend pour les taxonomies
 */

export type AgeBucketStructure = {
  id: string;
  is_child_meal: boolean;
};

export type AgeBucket = {
  id: string;
  label: string;
  is_child_meal: boolean;
};

export type AgeBucketsApiResponse = {
  buckets: AgeBucketStructure[];
};

export type AgeGroupLabels = Record<string, { label: string }>;

/**
 * Fetch age bucket structure from /api/v1/age-buckets/
 */
export async function fetchAgeBucketStructure(): Promise<AgeBucketStructure[] | null> {
  try {
    const res = await fetch("/api/v1/age-buckets/", {
      headers: { Accept: "application/json" },
    });

    if (!res.ok) {
      console.warn("Failed to fetch age buckets structure", { status: res.status });
      return null;
    }

    const data = (await res.json()) as unknown;
    if (!data || typeof data !== "object") return null;

    const buckets = (data as AgeBucketsApiResponse).buckets;
    if (!Array.isArray(buckets)) return null;

    return buckets;
  } catch (err) {
    console.warn("Error fetching age buckets structure", err);
    return null;
  }
}

/**
 * Combine structure (from /api/v1/age-buckets/) with labels (from i18n payload).
 *
 * @param structure - Array of { id, is_child_meal } from age-buckets API
 * @param labels - age_groups object from i18n API: { baby: { label: "..." }, ... }
 * @returns Combined AgeBucket[] with id, label, is_child_meal or null if invalid
 */
export function combineAgeBuckets(
  structure: AgeBucketStructure[] | null,
  labels: AgeGroupLabels | null | undefined
): AgeBucket[] | null {
  if (!structure || !labels) return null;

  const result: AgeBucket[] = [];

  for (const bucket of structure) {
    const labelEntry = labels[bucket.id];
    if (!labelEntry || typeof labelEntry.label !== "string") {
      console.warn(`Missing label for age bucket: ${bucket.id}`);
      return null; // Strict: all buckets must have labels
    }

    result.push({
      id: bucket.id,
      label: labelEntry.label,
      is_child_meal: bucket.is_child_meal,
    });
  }

  return result.length > 0 ? result : null;
}
